import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Avatar from './Avatar';
import { Card, Button, Modal } from './ui';

const GroupFeed = ({ group, onClose }) => {
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [postComments, setPostComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostId, setSharePostId] = useState(null);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagPostId, setFlagPostId] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [flagDescription, setFlagDescription] = useState('');
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);

  // Text formatting state
  const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
  const textareaRef = useRef(null);

  // Reply state
  const [replyingTo, setReplyingTo] = useState(null); // commentId being replied to
  const [replyTexts, setReplyTexts] = useState({}); // reply text for each comment

  useEffect(() => {
    if (group?.id) {
      console.log('🔄 GroupFeed component mounted for group:', group.name);
      console.log('👤 Current user:', user);
      fetchGroupPosts();
    }
  }, [group?.id]);

  const fetchGroupPosts = async () => {
    if (!group?.id) return;
    
    try {
      console.log(`🔄 Fetching posts for group: ${group.id}`);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/groups/${group.id}/posts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log(`📰 Group posts received: ${response.data.length}`);
      setPosts(response.data);
    } catch (error) {
      console.error('❌ Error fetching group posts:', error);
      console.error('❌ Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const createGroupPost = async () => {
    if (!newPost.trim() && selectedFiles.length === 0) return;

    try {
      console.log('📝 Creating group post...');
      console.log('📁 Files to upload:', selectedFiles.length);
      
      // Upload files first if any
      let attachmentIds = [];
      if (selectedFiles.length > 0) {
        setUploadingFiles(true);
        console.log('🔄 Starting file upload process...');
        
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          console.log(`📤 Uploading file ${i + 1}/${selectedFiles.length}: ${file.name}`);
          
          try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', 'posts');
            
            // Update progress for current file
            const currentProgress = (i / selectedFiles.length) * 100;
            setFileUploadProgress(Math.round(currentProgress));
            
            const uploadResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL || ''}/api/files/upload`, formData, {
              headers: { 
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data' 
              }
            });
            
            console.log(`✅ File ${i + 1} uploaded successfully:`, uploadResponse.data.file.id);
            attachmentIds.push(uploadResponse.data.file.id);
            
            // Update progress after successful upload
            const progressAfterUpload = ((i + 1) / selectedFiles.length) * 100;
            setFileUploadProgress(Math.round(progressAfterUpload));
            
          } catch (fileError) {
            console.error(`❌ Failed to upload file ${i + 1} (${file.name}):`, fileError);
            setUploadingFiles(false);
            
            // Provide specific error message to user
            const errorMessage = fileError.response?.data?.detail || 
                               fileError.response?.data?.message || 
                               `Failed to upload ${file.name}`;
            alert(`Upload Error: ${errorMessage}`);
            return; // Exit function on file upload error
          }
        }
        
        console.log('✅ All files uploaded successfully');
      }

      // Create post with attachments
      console.log('📝 Creating post with attachments:', attachmentIds);

      // Extract hashtags and mentions
      const hashtagRegex = /#(\w+)/g;
      const mentionRegex = /@(\w+)/g;
      const hashtags = [...newPost.matchAll(hashtagRegex)].map(match => match[1]);
      const mentions = [...newPost.matchAll(mentionRegex)].map(match => match[1]);

      const postData = {
        content: newPost,
        post_type: selectedFiles.some(f => f.type.startsWith('image/')) ? 'image' : 
                   selectedFiles.some(f => f.type.startsWith('video/')) ? 'video' : 'text',
        attachments: attachmentIds,
        hashtags: hashtags,
        mentions: mentions
      };

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL || ''}/api/groups/${group.id}/posts`, postData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('✅ Group post created:', response.data);
      
      // Add new post to the top of the list
      setPosts(prevPosts => [response.data, ...prevPosts]);
      
      // Clear form and reset states
      setNewPost('');
      setSelectedFiles([]);
      setFileUploadProgress(0);
      setUploadingFiles(false);
      setShowFormattingToolbar(false);
      
      console.log('🎉 Group post created successfully');
      
    } catch (error) {
      console.error('❌ Error creating group post:', error);
      
      // Reset upload states on error
      setUploadingFiles(false);
      setFileUploadProgress(0);
      
      // Provide specific error message to user
      const errorMessage = error.response?.data?.detail || 
                         error.response?.data?.message || 
                         'Failed to create post. Please try again.';
      alert(`Post Creation Error: ${errorMessage}`);
    }
  };

  const likePost = async (postId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/groups/${group.id}/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('👍 Group post like response:', response.data);
      
      // Update posts with new like count
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, likes_count: response.data.liked ? post.likes_count + 1 : post.likes_count - 1 }
            : post
        )
      );
    } catch (error) {
      console.error('❌ Error liking group post:', error);
    }
  };

  const fetchPostComments = async (postId) => {
    if (postComments[postId]) {
      // Toggle comments visibility
      setPostComments(prev => ({ ...prev, [postId]: null }));
      return;
    }

    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/groups/${group.id}/posts/${postId}/comments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPostComments(prev => ({ ...prev, [postId]: response.data }));
      console.log(`💬 Loaded ${response.data.length} comments for group post ${postId}`);
    } catch (error) {
      console.error('❌ Error fetching group comments:', error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const addComment = async (postId, content, parentCommentId = null) => {
    if (!content.trim()) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/groups/${group.id}/posts/${postId}/comments`, {
        content: content.trim(),
        parent_comment_id: parentCommentId
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Add comment to the list
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), response.data]
      }));

      // Update post comments count
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, comments_count: post.comments_count + 1 }
            : post
        )
      );

      // Clear reply state if it was a reply
      if (parentCommentId) {
        setReplyingTo(null);
        setReplyTexts(prev => ({ ...prev, [parentCommentId]: '' }));
      }

      console.log('💬 Comment added to group post');
    } catch (error) {
      console.error('❌ Error adding comment to group post:', error);
    }
  };

  const handleReply = (commentId) => {
    setReplyingTo(commentId);
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setReplyTexts({});
  };

  // Text formatting functions
  const formatText = (type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = newPost.substring(start, end);
    
    let formattedText;
    switch (type) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        break;
      default:
        return;
    }
    
    const newContent = newPost.substring(0, start) + formattedText + newPost.substring(end);
    setNewPost(newContent);
  };

  const insertSpecialText = (type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    let insertText;
    
    switch (type) {
      case 'hashtag':
        insertText = '#';
        break;
      case 'mention':
        insertText = '@';
        break;
      case 'link':
        insertText = '[Link Text](URL)';
        break;
      default:
        return;
    }
    
    const newContent = newPost.substring(0, start) + insertText + newPost.substring(start);
    setNewPost(newContent);
  };

  // File handling
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    
    for (const file of files) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isDocument = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain'
      ].includes(file.type);
      
      if (!isImage && !isVideo && !isDocument) {
        alert(`File "${file.name}" is not supported. Please upload images, videos, or documents only.`);
        continue;
      }
      
      // Validate file size (10MB for images/videos, 25MB for documents)
      const maxSize = (isImage || isVideo) ? 10 * 1024 * 1024 : 25 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024);
        alert(`File "${file.name}" is too large. Maximum size is ${maxMB}MB.`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                <div>
                  <div className="h-4 bg-gray-300 rounded-lg w-32 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded-lg w-24"></div>
                </div>
              </div>
              <Button onClick={onClose} variant="outline" size="sm">✕</Button>
            </div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-300 rounded-lg w-1/4 mb-2"></div>
                      <div className="h-2 bg-gray-300 rounded-lg w-1/6"></div>
                    </div>
                  </div>
                  <div className="h-20 bg-gray-300 rounded-lg mb-4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                {group?.name?.charAt(0) || 'G'}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{group?.name} Feed</h2>
                <p className="text-sm text-gray-500">{posts.length} posts • {group?.member_count} members</p>
              </div>
            </div>
            <Button onClick={onClose} variant="outline" size="sm">✕ Close</Button>
          </div>
        </div>

        <div className="p-6">
          {/* Post Creation */}
          <Card className="mb-6" padding="lg">
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <Avatar user={user} size="medium" />
                <div className="flex-1">
                  <textarea
                    ref={textareaRef}
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    onFocus={() => setShowFormattingToolbar(true)}
                    placeholder={`Share something with ${group?.name}...`}
                    className="w-full border-none resize-none text-gray-900 placeholder-gray-400 focus:outline-none text-lg"
                    rows="3"
                  />
                </div>
              </div>

              {/* Text Formatting Toolbar */}
              {showFormattingToolbar && (
                <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <button onClick={() => formatText('bold')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Bold">
                    <strong>B</strong>
                  </button>
                  <button onClick={() => formatText('italic')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Italic">
                    <em>I</em>
                  </button>
                  <button onClick={() => formatText('underline')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Underline">
                    <u>U</u>
                  </button>
                  <div className="w-px h-6 bg-gray-300"></div>
                  <button onClick={() => insertSpecialText('hashtag')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Hashtag">
                    #️⃣
                  </button>
                  <button onClick={() => insertSpecialText('mention')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Mention">
                    @
                  </button>
                  <button onClick={() => insertSpecialText('link')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Link">
                    🔗
                  </button>
                </div>
              )}

              {/* File Upload */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    id="group-file-upload"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label 
                    htmlFor="group-file-upload"
                    className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 cursor-pointer transition-all duration-300 rounded-xl border border-gray-200 hover:border-teal-300"
                  >
                    <span className="text-xl">📎</span>
                    <span className="font-medium">Attach Files</span>
                  </label>
                  <label 
                    htmlFor="group-file-upload"
                    className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 cursor-pointer transition-all duration-300 rounded-xl border border-gray-200 hover:border-purple-300"
                  >
                    <span className="text-xl">📷</span>
                    <span className="font-medium">Photos</span>
                  </label>
                  {uploadingFiles && (
                    <div className="text-sm text-gray-600">
                      Uploading... {Math.round(fileUploadProgress)}%
                    </div>
                  )}
                </div>
                <Button
                  onClick={createGroupPost}
                  disabled={(!newPost.trim() && selectedFiles.length === 0) || uploadingFiles}
                  className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white px-6 py-2"
                >
                  Post to Group
                </Button>
              </div>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Selected Files:</p>
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">
                            {file.type.startsWith('image/') ? '🖼️' : 
                             file.type === 'application/pdf' ? '📄' : 
                             file.type.includes('word') ? '📝' : 
                             file.type.includes('excel') ? '📊' : '📄'}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">{file.name}</p>
                            <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        >
                          ❌
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Posts Feed */}
          <div className="space-y-6">
            {posts.length === 0 ? (
              <Card className="text-center py-12">
                <div className="text-4xl mb-4">💬</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No posts yet</h3>
                <p className="text-gray-500">Be the first to share something with this group!</p>
              </Card>
            ) : (
              posts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-all duration-300" padding="none">
                  {/* Post Header */}
                  <div className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <Avatar 
                        user={post.user_id === user?.id ? user : post.user} 
                        size="medium" 
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-gray-900">{post.user?.full_name || 'Unknown User'}</h4>
                          <span className="text-gray-500">•</span>
                          <span className="text-sm text-gray-500">{formatTimeAgo(post.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-500">Posted in {group?.name}</p>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="mb-4">
                      <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{post.content}</p>
                      
                      {/* File Attachments */}
                      {post.attachments && post.attachments.length > 0 && (
                        <div className="mt-4">
                          <div className="grid gap-3">
                            {post.attachments.map((attachmentId, index) => (
                              <div key={index} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                                <FileAttachmentDisplay attachmentId={attachmentId} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Post Actions */}
                    <div className="flex items-center space-x-6 py-3 border-t border-gray-100">
                      <button
                        onClick={() => likePost(post.id)}
                        className="flex items-center space-x-2 text-gray-500 hover:text-red-500 transition-colors"
                      >
                        <span>❤️</span>
                        <span className="text-sm">{post.likes_count}</span>
                      </button>
                      
                      <button
                        onClick={() => fetchPostComments(post.id)}
                        className="flex items-center space-x-2 text-gray-500 hover:text-blue-500 transition-colors"
                      >
                        <span>💬</span>
                        <span className="text-sm">
                          {loadingComments[post.id] ? 'Loading...' : 
                           postComments[post.id] ? 'Hide Comments' :
                           `${post.comments_count} Comments`}
                        </span>
                      </button>
                      
                      <button
                        onClick={() => {setSharePostId(post.id); setShowShareModal(true);}}
                        className="flex items-center space-x-2 text-gray-500 hover:text-green-500 transition-colors"
                      >
                        <span>🔗</span>
                        <span className="text-sm">Share</span>
                      </button>
                    </div>
                  </div>

                  {/* Comments Section */}
                  {postComments[post.id] && (
                    <div className="bg-gray-50 p-6 border-t border-gray-200">
                      <div className="space-y-4">
                        {postComments[post.id]
                          .filter(comment => !comment.parent_comment_id) // Only show top-level comments
                          .map((comment) => (
                          <div key={comment.id} className="space-y-3">
                            {/* Main Comment */}
                            <div className="flex items-start space-x-3">
                              <Avatar 
                                user={comment.user_id === user?.id ? user : comment.user} 
                                size="small" 
                              />
                              <div className="flex-1">
                                <div className="bg-white rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-medium text-gray-900 text-sm">{comment.user?.full_name || 'Unknown User'}</span>
                                      <span className="text-xs text-gray-500">{formatTimeAgo(comment.created_at)}</span>
                                    </div>
                                    <button
                                      onClick={() => handleReply(comment.id)}
                                      className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                    >
                                      Reply
                                    </button>
                                  </div>
                                  <p className="text-gray-700 text-sm">{comment.content}</p>
                                </div>
                                
                                {/* Reply Input for this comment */}
                                {replyingTo === comment.id && (
                                  <div className="mt-3 ml-3">
                                    <div className="flex items-start space-x-2">
                                      <Avatar user={user} size="small" className="w-6 h-6" />
                                      <div className="flex-1">
                                        <input
                                          type="text"
                                          placeholder={`Reply to ${comment.user?.full_name}...`}
                                          value={replyTexts[comment.id] || ''}
                                          onChange={(e) => setReplyTexts(prev => ({ ...prev, [comment.id]: e.target.value }))}
                                          className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                              addComment(post.id, e.target.value, comment.id);
                                            }
                                          }}
                                        />
                                        <div className="flex items-center space-x-2 mt-2">
                                          <button
                                            onClick={() => {
                                              if (replyTexts[comment.id]?.trim()) {
                                                addComment(post.id, replyTexts[comment.id], comment.id);
                                              }
                                            }}
                                            disabled={!replyTexts[comment.id]?.trim()}
                                            className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            Reply
                                          </button>
                                          <button
                                            onClick={cancelReply}
                                            className="text-xs text-gray-600 hover:text-gray-800 px-3 py-1 rounded hover:bg-gray-100"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Nested Replies */}
                            {postComments[post.id]
                              .filter(reply => reply.parent_comment_id === comment.id)
                              .map((reply) => (
                              <div key={reply.id} className="flex items-start space-x-3 ml-8">
                                <Avatar 
                                  user={reply.user_id === user?.id ? user : reply.user} 
                                  size="small" 
                                  className="w-6 h-6" 
                                />
                                <div className="flex-1">
                                  <div className="bg-white rounded-lg p-3 border-l-2 border-blue-200">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="font-medium text-gray-900 text-sm">{reply.user?.full_name || 'Unknown User'}</span>
                                      <span className="text-xs text-gray-500">{formatTimeAgo(reply.created_at)}</span>
                                    </div>
                                    <p className="text-gray-700 text-sm">{reply.content}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                        
                        {/* Add Comment */}
                        <div className="flex items-start space-x-3 mt-4">
                          <Avatar user={user} size="small" />
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Write a comment..."
                              className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && e.target.value.trim()) {
                                  addComment(post.id, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// File Attachment Display Component for Group Feed
const FileAttachmentDisplay = ({ attachmentId }) => {
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFileInfo();
  }, [attachmentId]);

  const fetchFileInfo = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/files/${attachmentId}/info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFileInfo(response.data);
    } catch (error) {
      console.error('Error fetching file info:', error);
      setError('Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (fileInfo) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const downloadUrl = `${backendUrl}/api/files/${attachmentId}`;
      window.open(downloadUrl, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6 bg-gray-50">
        <div className="animate-spin text-2xl">⏳</div>
        <span className="ml-3 text-gray-600">Loading file...</span>
      </div>
    );
  }

  if (error || !fileInfo) {
    return (
      <div className="flex items-center justify-center p-6 bg-red-50 text-red-600">
        <span className="text-2xl mr-3">❌</span>
        <span>{error || 'File not found'}</span>
      </div>
    );
  }

  const isImage = fileInfo.file_type === 'image';

  return (
    <div className="bg-white">
      {isImage ? (
        /* Image Display */
        <div className="relative group">
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/files/${attachmentId}`}
            alt={fileInfo.original_filename}
            className="w-full h-auto max-h-96 object-cover rounded-lg"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center p-8 bg-gray-100 rounded-lg">
            <span className="text-4xl mr-4">🖼️</span>
            <div className="text-center">
              <p className="font-medium text-gray-900">{fileInfo.original_filename}</p>
              <p className="text-sm text-gray-500">Image could not be displayed</p>
            </div>
          </div>
          {/* Image Overlay with Download */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
            <button
              onClick={handleDownload}
              className="bg-white text-gray-900 px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <span className="mr-2">📥</span>
              Download
            </button>
          </div>
        </div>
      ) : (
        /* File Display */
        <div 
          onClick={handleDownload}
          className="flex items-center p-4 hover:bg-gray-50 cursor-pointer transition-colors border rounded-lg"
        >
          <div className="text-4xl mr-4">
            {fileInfo.mime_type === 'application/pdf' ? '📄' : 
             fileInfo.mime_type?.includes('word') ? '📝' : 
             fileInfo.mime_type?.includes('excel') || fileInfo.mime_type?.includes('sheet') ? '📊' : 
             fileInfo.mime_type?.includes('powerpoint') || fileInfo.mime_type?.includes('presentation') ? '📊' : 
             fileInfo.mime_type?.startsWith('text/') ? '📄' : '📎'}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 truncate">
              {fileInfo.original_filename}
            </p>
            <p className="text-sm text-gray-500">
              {formatFileSize(fileInfo.file_size)} • 
              {fileInfo.file_type?.split('/')[1]?.toUpperCase() || 'FILE'}
            </p>
          </div>
          <div className="text-gray-400 hover:text-gray-600">
            <span className="text-2xl">📥</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupFeed;